import {
  Autorenew,
  CheckCircle,
  ConfirmationNumber,
  ErrorOutline,
  Fullscreen,
  InfoOutlined,
  InsertDriveFileOutlined,
  LocationOnOutlined,
  Pause,
  PlayArrow,
  PlayCircleOutline,
  ReplyOutlined,
  StickyNote2Outlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grow,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useEffect, useRef, useState, type ReactElement } from "react";
import {
  MessageModel,
  MessageTypeEnum,
} from "../../data/models/Chat/MessageModel";

interface MessageItemProps {
  message: MessageModel;
  toolDisplayMode?: "legacy" | "compact" | "debug";
  isAssistant?: boolean;
  onRequestAudioTranscription?: (message: MessageModel) => Promise<void>;
}

const safeParse = (jsonStr?: unknown): any => {
  if (jsonStr && typeof jsonStr === "object") {
    return jsonStr;
  }
  if (typeof jsonStr !== "string") {
    return null;
  }
  try {
    return jsonStr ? JSON.parse(jsonStr) : null;
  } catch {
    return null;
  }
};

const resolveMediaUrl = (message: MessageModel, metadata: any): string => {
  const content = typeof message.content === "string" ? message.content : "";
  if (typeof metadata?.mediaUrl === "string" && metadata.mediaUrl) {
    return metadata.mediaUrl;
  }
  if (
    content.startsWith("http") ||
    content.startsWith("blob:") ||
    content.startsWith("data:")
  ) {
    return content;
  }
  return "";
};

const isGifMedia = (message: MessageModel, metadata: any): boolean => {
  const content = typeof message.content === "string" ? message.content : "";
  const mediaUrl = typeof metadata?.mediaUrl === "string" ? metadata.mediaUrl : "";
  const mimeType = String(metadata?.mimeType || metadata?.mime_type || "").toLowerCase();
  const mediaKind = String(metadata?.mediaKind || "").toLowerCase();
  return (
    message.messageType === MessageTypeEnum.VIDEO &&
    (mediaKind === "gif" ||
      Boolean(metadata?.gif) ||
      Boolean(metadata?.isGif) ||
      Boolean(metadata?.animated) ||
      mimeType.includes("gif") ||
      /\.gif(?:[?#].*)?$/i.test(content) ||
      /\.gif(?:[?#].*)?$/i.test(mediaUrl))
  );
};

const MediaPendingState = ({
  failed,
  label,
  loader,
}: {
  failed?: boolean;
  label: string;
  loader?: boolean;
}) =>
  failed ? (
    <Typography variant="body2" color="text.secondary">
      {`No se pudo procesar ${label}.`}
    </Typography>
  ) : loader ? (
    <Box
      sx={{
        width: 180,
        height: 180,
        display: "grid",
        placeItems: "center",
      }}
    >
      <CircularProgress size={28} thickness={4.5} />
    </Box>
  ) : (
    <Typography variant="body2" color="text.secondary">
      {`${label} en proceso.`}
    </Typography>
  );

const formatAudioTime = (value: number) => {
  if (!Number.isFinite(value) || value < 0) {
    return "0:00";
  }
  const minutes = Math.floor(value / 60);
  const seconds = Math.floor(value % 60);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

const audioWaveBars = [
  10, 16, 7, 22, 13, 18, 28, 11, 20, 14, 24, 9, 17, 26, 12, 21, 15, 30, 18,
  10, 23, 13, 19, 27, 11, 16, 24, 14, 20, 8, 18, 25,
];

const AudioMiniPlayer = ({
  src,
  failed,
  isAssistant,
}: {
  src?: string;
  failed?: boolean;
  isAssistant?: boolean;
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const isReady = Boolean(src) && !failed;
  const progress =
    isReady && duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;

  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, [src]);

  const togglePlayback = async () => {
    if (!audioRef.current || !isReady) {
      return;
    }
    if (audioRef.current.paused) {
      await audioRef.current.play();
      setIsPlaying(true);
      return;
    }
    audioRef.current.pause();
    setIsPlaying(false);
  };

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 272,
        maxWidth: 272,
        px: 0.75,
        py: 0.55,
        display: "flex",
        alignItems: "center",
        gap: 0.65,
        borderRadius: "15px",
        borderBottomRightRadius: isAssistant ? "5px" : "15px",
        borderBottomLeftRadius: isAssistant ? "15px" : "5px",
        bgcolor: "rgba(255, 255, 255, 0.97)",
        color: "#0f172a",
        boxShadow:
          "0 5px 16px rgba(63, 18, 92, 0.16), inset 0 0 0 1px rgba(15, 23, 42, 0.05)",
      }}
    >
      {src && (
        <audio
          ref={audioRef}
          src={src}
          preload="metadata"
          onLoadedMetadata={(event) => setDuration(event.currentTarget.duration)}
          onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
          onEnded={() => setIsPlaying(false)}
        />
      )}
      <Button
        type="button"
        onClick={togglePlayback}
        disabled={!isReady}
        sx={{
          minWidth: 29,
          width: 29,
          height: 29,
          p: 0,
          borderRadius: "50%",
          bgcolor: failed ? "rgba(239, 68, 68, 0.14)" : "#ffffff",
          color: failed ? "#b91c1c" : "#111827",
          boxShadow: "0 3px 12px rgba(15, 23, 42, 0.16)",
          "&:hover": { bgcolor: "#f8fafc" },
          "&.Mui-disabled": {
            bgcolor: failed ? "rgba(239, 68, 68, 0.14)" : "#f1f5f9",
            color: failed ? "#b91c1c" : "#64748b",
          },
        }}
      >
        {failed ? (
          <ErrorOutline sx={{ fontSize: 16 }} />
        ) : isReady ? (
          isPlaying ? (
            <Pause sx={{ fontSize: 16 }} />
          ) : (
            <PlayArrow sx={{ fontSize: 17 }} />
          )
        ) : (
          <CircularProgress size={14} thickness={5} sx={{ color: "#8e24aa" }} />
        )}
      </Button>
      <Typography
        variant="caption"
        sx={{
          minWidth: 47,
          color: failed ? "#b91c1c" : "#0f172a",
          fontWeight: 900,
          lineHeight: 1,
          letterSpacing: -0.15,
          whiteSpace: "nowrap",
        }}
      >
        {failed
          ? "Error"
          : isReady
          ? `${formatAudioTime(currentTime)} / ${formatAudioTime(duration)}`
          : "Audio"}
      </Typography>
      <Box sx={{ flex: "0 0 126px", width: 126, minWidth: 126 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.25,
            height: 26,
            "@keyframes audioWavePulse": {
              "0%, 100%": { opacity: 0.28, transform: "scaleY(0.65)" },
              "50%": { opacity: 0.8, transform: "scaleY(1)" },
            },
          }}
        >
          {audioWaveBars.map((height, index) => {
            const barProgress = ((index + 1) / audioWaveBars.length) * 100;
            const isActive = isReady && progress >= barProgress;
            return (
              <Box
                key={`${height}-${index}`}
                sx={{
                  width: 2.5,
                  height: height * 0.85,
                  borderRadius: 999,
                  flex: "0 0 2.5px",
                  bgcolor: failed
                    ? "rgba(239, 68, 68, 0.42)"
                    : isActive
                    ? "#0f172a"
                    : "rgba(15, 23, 42, 0.3)",
                  opacity: isReady || failed ? 1 : 0.45,
                  transformOrigin: "center",
                  animation:
                    isReady || failed
                      ? "none"
                      : `audioWavePulse 1.1s ease-in-out ${index * 0.035}s infinite`,
                }}
              />
            );
          })}
        </Box>
        {!isReady && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: failed ? "#b91c1c" : "#475569",
              mt: -0.35,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            {failed ? "No se pudo preparar" : "Preparando..."}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const GifPreview = ({
  src,
  caption,
}: {
  src: string;
  caption?: string;
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const isGifImage = /\.gif(?:[?#].*)?$/i.test(src);

  const togglePlayback = async () => {
    if (isGifImage || !videoRef.current) {
      return;
    }
    if (videoRef.current.paused) {
      await videoRef.current.play();
      setIsPlaying(true);
      return;
    }
    videoRef.current.pause();
    setIsPlaying(false);
  };

  return (
    <Box sx={{ maxWidth: 300 }}>
      <Box
        role="button"
        tabIndex={0}
        onClick={togglePlayback}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            void togglePlayback();
          }
        }}
        sx={{
          position: "relative",
          lineHeight: 0,
          overflow: "hidden",
          borderRadius: "18px",
          cursor: isGifImage ? "default" : "pointer",
          bgcolor: "#111827",
        }}
      >
        {isGifImage ? (
          <img
            src={src}
            alt={caption || "GIF"}
            style={{
              width: "100%",
              maxHeight: 300,
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : (
          <video
            ref={videoRef}
            src={src}
            loop
            muted
            playsInline
            preload="metadata"
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            style={{
              width: "100%",
              maxHeight: 300,
              objectFit: "cover",
              display: "block",
            }}
          />
        )}
        {(!isPlaying || isGifImage) && (
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              px: 1.4,
              py: 0.55,
              borderRadius: 999,
              bgcolor: "rgba(17, 24, 39, 0.72)",
              color: "#ffffff",
              fontSize: 13,
              fontWeight: 900,
              letterSpacing: 0.8,
              lineHeight: 1,
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.25)",
            }}
          >
            GIF
          </Box>
        )}
      </Box>
      {caption && (
        <Typography variant="body2" sx={{ mt: 0.75 }}>
          {caption}
        </Typography>
      )}
    </Box>
  );
};

const VideoPendingPreview = ({
  failed,
  isGif,
}: {
  failed?: boolean;
  isGif?: boolean;
}) => (
  <Box
    sx={{
      width: 260,
      maxWidth: "100%",
      aspectRatio: "1.55 / 1",
      position: "relative",
      overflow: "hidden",
      borderRadius: "18px",
      bgcolor: failed ? "rgba(127, 29, 29, 0.92)" : "#171124",
      boxShadow: "inset 0 0 0 1px rgba(255, 255, 255, 0.08)",
      backgroundImage: failed
        ? "none"
        : "línear-gradient(135deg, rgba(255,255,255,0.08), transparent 42%), radial-gradient(circle at 30% 20%, rgba(255,255,255,0.15), transparent 26%)",
      "&::before": failed
        ? {}
        : {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "línear-gradient(100deg, transparent 0%, rgba(255,255,255,0.14) 45%, transparent 78%)",
            transform: "translateX(-100%)",
            animation: "videoPendingSweep 1.5s ease-in-out infinite",
          },
      "@keyframes videoPendingSweep": {
        "0%": { transform: "translateX(-100%)" },
        "100%": { transform: "translateX(100%)" },
      },
    }}
  >
    <Box
      sx={{
        position: "absolute",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        width: 52,
        height: 52,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        bgcolor: "rgba(255, 255, 255, 0.94)",
        color: failed ? "#b91c1c" : "#8a00cc",
        boxShadow: "0 10px 26px rgba(0, 0, 0, 0.22)",
      }}
    >
      {failed ? (
        <ErrorOutline sx={{ fontSize: 25 }} />
      ) : (
        <CircularProgress size={24} thickness={5} sx={{ color: "#8a00cc" }} />
      )}
    </Box>
    <Box
      sx={{
        position: "absolute",
        left: 10,
        bottom: 10,
        px: 1,
        py: 0.45,
        borderRadius: 999,
        bgcolor: "rgba(15, 23, 42, 0.72)",
        color: "#ffffff",
        fontSize: 11,
        fontWeight: 900,
        letterSpacing: 0.55,
        lineHeight: 1,
      }}
    >
      {failed ? "ERROR" : isGif ? "GIF" : "VIDEO"}
    </Box>
    {!failed && (
      <Typography
        sx={{
          position: "absolute",
          right: 10,
          bottom: 9,
          color: "rgba(255,255,255,0.82)",
          fontSize: 11,
          fontWeight: 700,
          lineHeight: 1,
        }}
      >
        Preparando...
      </Typography>
    )}
  </Box>
);

const VideoPlayerPreview = ({
  src,
  caption,
  isAssistant,
}: {
  src: string;
  caption?: string;
  isAssistant?: boolean;
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [openVideoDialog, setOpenVideoDialog] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const progress =
    duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;

  const togglePlayback = async () => {
    if (!videoRef.current) {
      return;
    }
    if (videoRef.current.paused) {
      await videoRef.current.play();
      setIsPlaying(true);
      return;
    }
    videoRef.current.pause();
    setIsPlaying(false);
  };

  const seekVideo = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!videoRef.current || duration <= 0) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const nextProgress = Math.min(
      1,
      Math.max(0, (event.clientX - rect.left) / rect.width),
    );
    videoRef.current.currentTime = nextProgress * duration;
    setCurrentTime(videoRef.current.currentTime);
  };

  return (
    <Box sx={{ width: 292, maxWidth: "100%" }}>
      <Box
        role="button"
        tabIndex={0}
        onClick={togglePlayback}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            void togglePlayback();
          }
        }}
        sx={{
          position: "relative",
          overflow: "hidden",
          lineHeight: 0,
          borderRadius: "18px",
          borderBottomRightRadius: isAssistant ? "6px" : "18px",
          borderBottomLeftRadius: isAssistant ? "18px" : "6px",
          bgcolor: "#111827",
          cursor: "pointer",
          boxShadow: "0 8px 22px rgba(32, 12, 48, 0.22)",
        }}
      >
        <video
          ref={videoRef}
          src={src}
          preload="metadata"
          playsInline
          onLoadedMetadata={(event) => setDuration(event.currentTarget.duration)}
          onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
          style={{
            width: "100%",
            maxHeight: 320,
            objectFit: "cover",
            display: "block",
          }}
        />
        {!isPlaying && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              background:
                "línear-gradient(180deg, rgba(15,23,42,0.08), rgba(15,23,42,0.46))",
            }}
          >
            <Box
              sx={{
                width: 54,
                height: 54,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                bgcolor: "rgba(255,255,255,0.94)",
                color: "#8a00cc",
                boxShadow: "0 12px 28px rgba(0,0,0,0.28)",
              }}
            >
              <PlayArrow sx={{ fontSize: 34 }} />
            </Box>
          </Box>
        )}
        <Box
          onClick={(event) => {
            event.stopPropagation();
            setOpenVideoDialog(true);
          }}
          sx={{
            position: "absolute",
            top: 9,
            right: 9,
            width: 30,
            height: 30,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(15, 23, 42, 0.58)",
            color: "#ffffff",
            backdropFilter: "blur(8px)",
            opacity: isPlaying ? 0.86 : 1,
            transition: "opacity 160ms ease",
            "&:hover": {
              bgcolor: "rgba(15, 23, 42, 0.74)",
            },
          }}
        >
          <Fullscreen sx={{ fontSize: 20 }} />
        </Box>
        <Box
          onClick={(event) => {
            event.stopPropagation();
            seekVideo(event);
          }}
          sx={{
            position: "absolute",
            left: 10,
            right: 10,
            bottom: 9,
            height: 26,
            display: "flex",
            alignItems: "center",
            gap: 0.8,
            px: 0.75,
            borderRadius: 999,
            bgcolor: "rgba(15, 23, 42, 0.62)",
            backdropFilter: "blur(8px)",
            color: "#ffffff",
          }}
        >
          <Box
            sx={{
              width: 18,
              height: 18,
              display: "grid",
              placeItems: "center",
              flex: "0 0 auto",
            }}
          >
            {isPlaying ? (
              <Pause sx={{ fontSize: 16 }} />
            ) : (
              <PlayArrow sx={{ fontSize: 17 }} />
            )}
          </Box>
          <Box
            sx={{
              position: "relative",
              flex: 1,
              height: 4,
              borderRadius: 999,
              overflow: "hidden",
              bgcolor: "rgba(255,255,255,0.34)",
              "&::after": {
                content: '""',
                position: "absolute",
                inset: 0,
                width: `${progress}%`,
                bgcolor: "#ffffff",
                borderRadius: 999,
              },
            }}
          />
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 800,
              lineHeight: 1,
              minWidth: 34,
              textAlign: "right",
            }}
          >
            {formatAudioTime(duration || currentTime)}
          </Typography>
        </Box>
      </Box>
      <Dialog
        open={openVideoDialog}
        onClose={() => setOpenVideoDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#050816",
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxHeight: "86vh",
            display: "grid",
            placeItems: "center",
            bgcolor: "#050816",
          }}
        >
          <video
            src={src}
            controls
            autoPlay
            style={{
              width: "100%",
              maxHeight: "86vh",
              objectFit: "contain",
              display: "block",
            }}
          />
        </Box>
      </Dialog>
      {caption && (
        <Typography variant="body2" sx={{ mt: 0.75 }}>
          {caption}
        </Typography>
      )}
    </Box>
  );
};

const LocationPreview = ({
  latitude,
  longitude,
  name,
  address,
}: {
  latitude?: number | string;
  longitude?: number | string;
  name?: string;
  address?: string;
}) => {
  const lat = Number(latitude);
  const lng = Number(longitude);
  const hasCoordinates = Number.isFinite(lat) && Number.isFinite(lng);
  const mapsUrl = hasCoordinates
    ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
    : "";
  const embedUrl = hasCoordinates
    ? `https://maps.google.com/maps?q=${lat},${lng}&z=16&output=embed`
    : "";

  if (!hasCoordinates) {
    return (
      <Box
        sx={{
          width: 292,
          maxWidth: "100%",
          p: 1.2,
          borderRadius: "18px",
          bgcolor: "rgba(255,255,255,0.12)",
          color: "#ffffff",
          border: "1px solid rgba(255,255,255,0.18)",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(255,255,255,0.92)",
              color: "#8a00cc",
            }}
          >
            <LocationOnOutlined fontSize="small" />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 800 }}>
              Ubicacion recibida
            </Typography>
            <Typography sx={{ fontSize: 12.5, opacity: 0.82 }}>
              Preparando vista del mapa...
            </Typography>
          </Box>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 292,
        maxWidth: "100%",
        overflow: "hidden",
        borderRadius: "18px",
        bgcolor: "rgba(255,255,255,0.96)",
        color: "#0f172a",
        boxShadow: "0 8px 20px rgba(32, 12, 48, 0.16)",
      }}
    >
      <Box
        sx={{
          position: "relative",
          height: 168,
          overflow: "hidden",
          bgcolor: "#dbeafe",
        }}
      >
        <Box
          component="iframe"
          title="Ubicacion compartida"
          src={embedUrl}
          width="100%"
          height="100%"
          loading="lazy"
          sx={{
            border: 0,
            display: "block",
            filter: "saturate(0.9) contrast(1.02)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background:
              "línear-gradient(180deg, rgba(15,23,42,0.12), transparent 38%, rgba(15,23,42,0.18))",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "48%",
            transform: "translate(-50%, -50%)",
            width: 44,
            height: 44,
            borderRadius: "50% 50% 50% 8px",
            rotate: "-45deg",
            display: "grid",
            placeItems: "center",
            bgcolor: "#8a00cc",
            color: "#ffffff",
            boxShadow: "0 10px 24px rgba(138, 0, 204, 0.38)",
            border: "3px solid #ffffff",
          }}
        >
          <LocationOnOutlined sx={{ fontSize: 23, rotate: "45deg" }} />
        </Box>
        <Box
          sx={{
            position: "absolute",
            left: 10,
            top: 10,
            px: 1,
            py: 0.45,
            borderRadius: 999,
            bgcolor: "rgba(255,255,255,0.92)",
            color: "#8a00cc",
            fontSize: 11,
            fontWeight: 900,
            lineHeight: 1,
          }}
        >
          UBICACION
        </Box>
      </Box>
      <Box sx={{ p: 1.15 }}>
        <Typography sx={{ fontSize: 14.5, fontWeight: 900, lineHeight: 1.2 }}>
          {name || "Ubicacion compartida"}
        </Typography>
        <Typography
          sx={{
            mt: 0.35,
            fontSize: 12.5,
            color: "#475569",
            lineHeight: 1.25,
          }}
        >
          {address || `${lat.toFixed(6)}, ${lng.toFixed(6)}`}
        </Typography>
        <Button
          component="a"
          href={mapsUrl}
          target="_blank"
          rel="noreferrer"
          size="small"
          variant="contained"
          startIcon={<LocationOnOutlined />}
          sx={{
            mt: 1,
            px: 1.2,
            py: 0.35,
            minHeight: 28,
            borderRadius: 999,
            bgcolor: "#8a00cc",
            fontSize: 12,
            fontWeight: 800,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { bgcolor: "#7b1fa2", boxShadow: "none" },
          }}
        >
          Abrir mapa
        </Button>
      </Box>
    </Box>
  );
};

const getDocumentExtension = (fileName: string, mimeType?: string) => {
  const fromName = fileName.split(".").pop()?.trim();
  if (fromName && fromName !== fileName) return fromName.toUpperCase();
  const normalizedMime = String(mimeType || "").toLowerCase();
  if (normalizedMime.includes("pdf")) return "PDF";
  if (normalizedMime.includes("word")) return "DOC";
  if (normalizedMime.includes("excel") || normalizedMime.includes("sheet")) return "XLS";
  if (normalizedMime.includes("image")) return "IMG";
  return "DOC";
};

const DocumentPreview = ({
  fileUrl,
  fileName,
  mimeType,
  caption,
  failed,
}: {
  fileUrl?: string;
  fileName: string;
  mimeType?: string;
  caption?: string;
  failed?: boolean;
}) => {
  const extension = getDocumentExtension(fileName, mimeType);
  const isPdf = extension === "PDF";

  if (!fileUrl) {
    return (
      <Box
        sx={{
          width: 306,
          maxWidth: "100%",
          p: 1,
          borderRadius: "18px",
          bgcolor: "rgba(255,255,255,0.95)",
          color: "#0f172a",
          display: "flex",
          alignItems: "center",
          gap: 1,
          boxShadow: "0 8px 20px rgba(32, 12, 48, 0.14)",
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 58,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            bgcolor: failed ? "rgba(239,68,68,0.12)" : "rgba(138,0,204,0.1)",
            color: failed ? "#b91c1c" : "#8a00cc",
          }}
        >
          {failed ? <ErrorOutline /> : <CircularProgress size={22} thickness={5} />}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 900 }} noWrap>
            {failed ? "No se pudo preparar" : "Preparando documento"}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#64748b" }}>
            {fileName}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 320,
        maxWidth: "100%",
        overflow: "hidden",
        borderRadius: "18px",
        bgcolor: "rgba(255,255,255,0.96)",
        color: "#0f172a",
        boxShadow: "0 8px 20px rgba(32, 12, 48, 0.16)",
      }}
    >
      <Box
        sx={{
          height: 94,
          position: "relative",
          overflow: "hidden",
          bgcolor: isPdf ? "#fff1f2" : "#eef2ff",
          backgroundImage:
            "línear-gradient(135deg, rgba(255,255,255,0.78), transparent 52%), radial-gradient(circle at 82% 18%, rgba(138,0,204,0.12), transparent 24%)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: 16,
            top: 14,
            width: 58,
            height: 68,
            borderRadius: 2,
            bgcolor: "#ffffff",
            boxShadow: "0 10px 22px rgba(15,23,42,0.14)",
            border: "1px solid rgba(15,23,42,0.08)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: 18,
              bgcolor: isPdf ? "#ef4444" : "#8a00cc",
              color: "#ffffff",
              fontSize: 9,
              fontWeight: 900,
              display: "grid",
              placeItems: "center",
              letterSpacing: 0.6,
            }}
          >
            {extension}
          </Box>
          <Stack spacing={0.45} sx={{ p: 0.8 }}>
            {[28, 36, 24].map((width, index) => (
              <Box
                key={index}
                sx={{
                  width,
                  height: 3,
                  borderRadius: 999,
                  bgcolor: "rgba(100,116,139,0.28)",
                }}
              />
            ))}
          </Stack>
        </Box>
        <Box
          sx={{
            position: "absolute",
            left: 88,
            right: 14,
            top: 17,
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 900,
              lineHeight: 1.2,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              wordBreak: "break-word",
            }}
          >
            {fileName}
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 11.5, color: "#64748b" }}>
            Documento {extension}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ p: 1 }}>
        <Button
          component="a"
          href={fileUrl}
          target="_blank"
          rel="noreferrer"
          size="small"
          variant="contained"
          startIcon={<InsertDriveFileOutlined />}
          sx={{
            width: "100%",
            minHeight: 30,
            borderRadius: 999,
            bgcolor: "#8a00cc",
            fontSize: 12,
            fontWeight: 900,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { bgcolor: "#7b1fa2", boxShadow: "none" },
          }}
        >
          Abrir documento
        </Button>
        {caption && (
          <Typography sx={{ mt: 0.75, fontSize: 13, lineHeight: 1.35 }}>
            {caption}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

function StoresOpenedWidget({ data }: { data: { closestStore: any[] } }) {
  return (
    <Box>
      <Typography variant="body2" fontWeight="bold">
        Última actualización de las sedes abiertas:
      </Typography>
      {data.closestStore?.map((sede, idx) => (
        <Box key={idx} mt={1} p={1} border="1px solid #ccc" borderRadius={2}>
          <Typography variant="body2">
            <strong>{sede?.name || "Nombre no disponible"}</strong> -
            {sede?.opened
              ? `${sede.opened} a ${sede.closed}`
              : "Horario no disponible"}
          </Typography>
          <Typography variant="caption">
            Coordenadas: ({sede?.lat || "Lat no disponible"},{" "}
            {sede?.lng || "Lng no disponible"})
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function isJson(str: string) {
  try {
    const obj = JSON.parse(str);
    return typeof obj === "object" && obj !== null;
  } catch {
    return false;
  }
}

type ToolTone = {
  label: string;
  icon: ReactElement;
  color: string;
  background: string;
  border: string;
};

function getToolTone(content: string): ToolTone {
  if (/successfully transferred to/i.test(content)) {
    return {
      label: "Proceso",
      icon: <Autorenew fontSize="small" />,
      color: "#7DD3FC",
      background: "rgba(125, 211, 252, 0.12)",
      border: "rgba(125, 211, 252, 0.22)",
    };
  }

  if (/successfully transferred back/i.test(content)) {
    return {
      label: "Proceso",
      icon: <Autorenew fontSize="small" />,
      color: "#C084FC",
      background: "rgba(192, 132, 252, 0.12)",
      border: "rgba(192, 132, 252, 0.22)",
    };
  }

  if (/error|no se pudo|failed|faltan/i.test(content)) {
    return {
      label: "Error",
      icon: <ErrorOutline fontSize="small" />,
      color: "#FCA5A5",
      background: "rgba(248, 113, 113, 0.12)",
      border: "rgba(248, 113, 113, 0.22)",
    };
  }

  if (/enviado correctamente|agregu/i.test(content)) {
    return {
      label: "OK",
      icon: <CheckCircle fontSize="small" />,
      color: "#86EFAC",
      background: "rgba(74, 222, 128, 0.12)",
      border: "rgba(74, 222, 128, 0.22)",
    };
  }

  return {
    label: "Info",
    icon: <InfoOutlined fontSize="small" />,
    color: "#7DD3FC",
    background: "rgba(56, 189, 248, 0.12)",
    border: "rgba(56, 189, 248, 0.22)",
  };
}

function previewValue(value: unknown) {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return `Array(${value.length})`;
  }
  if (value && typeof value === "object") {
    return "Objeto";
  }
  return "Sin valor";
}

function summarizeToolContent(content: string) {
  const trimmedContent = content.trim();

  if (isJson(trimmedContent)) {
    const parsed = JSON.parse(trimmedContent) as Record<string, unknown>;
    const titleCandidate = [
      parsed.type,
      parsed.event,
      parsed.action,
      parsed.name,
      parsed.status,
    ].find((value) => typeof value === "string" && value.trim());
    const title = titleCandidate ? String(titleCandidate) : "Evento técnico";
    const detailCandidate = [
      parsed.message,
      parsed.description,
      parsed.detail,
      parsed.text,
    ].find((value) => typeof value === "string" && value.trim());
    const fallbackDetail = Object.entries(parsed)
      .slice(0, 3)
      .map(([key, value]) => `${key}: ${previewValue(value)}`)
      .join(" · ");

    return {
      title,
      detail:
        detailCandidate && String(detailCandidate).trim()
          ? String(detailCandidate).trim()
          : fallbackDetail || "Detalles técnicos disponibles",
      raw: JSON.stringify(parsed, null, 2),
      isJson: true,
    };
  }

  const compactTitle = trimmedContent.split(/\r?\n/)[0] || "Evento técnico";
  const detail =
    trimmedContent.length > 180
      ? `${trimmedContent.slice(0, 180).trimEnd()}...`
      : trimmedContent || "Sin contenido";

  return {
    title:
      compactTitle.length > 90
        ? `${compactTitle.slice(0, 90).trimEnd()}...`
        : compactTitle,
    detail,
    raw: trimmedContent,
    isJson: false,
  };
}

function getInteractiveOptions(metadata?: unknown): string[] {
  const parsed = safeParse(metadata);
  const buttonCandidates =
    parsed?.buttons ||
    parsed?.interactive?.action?.buttons ||
    parsed?.action?.buttons ||
    parsed?.options;

  if (!Array.isArray(buttonCandidates)) return [];

  return buttonCandidates
    .map((button) => {
      if (typeof button === "string") return button;
      return (
        button?.title ||
        button?.label ||
        button?.text ||
        button?.reply?.title ||
        button?.button_reply?.title
      );
    })
    .filter((value): value is string => typeof value === "string" && Boolean(value.trim()));
}

const InteractiveMessage = ({ message }: { message: MessageModel }) => {
  const options = getInteractiveOptions(message.contentMetadata);

  return (
    <Stack spacing={options.length ? 0.8 : 0}>
      <Typography variant="body2">{message.content}</Typography>
      {options.length > 0 && (
        <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
          {options.map((option) => (
            <Chip
              key={option}
              size="small"
              label={option}
              variant="outlined"
              sx={{
                height: 24,
                borderColor: "currentColor",
                color: "inherit",
                opacity: 0.82,
                "& .MuiChip-label": {
                  px: 0.85,
                  fontSize: 12,
                  fontWeight: 700,
                },
              }}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

const ToolCompactMessage = ({ content }: { content: string }) => {
  const tone = getToolTone(content);
  const summary = summarizeToolContent(content);

  return (
    <Grow in timeout={180}>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
          mb: 0.35,
          mt: -0.15,
        }}
      >
        <Chip
          size="small"
          label="Ejec."
          icon={
            <Box
              sx={{
                width: 14,
                height: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: tone.color,
                ml: 0.15,
              }}
            >
              {tone.icon}
            </Box>
          }
          title={summary.title}
          sx={{
            height: 20,
            px: 0.35,
            borderRadius: 999,
            border: "1px solid",
            borderColor: tone.border,
            bgcolor: tone.background,
            color: tone.color,
            "& .MuiChip-label": {
              px: 0.55,
              py: 0,
              fontSize: 9.5,
              fontWeight: 800,
              lineHeight: 1,
              letterSpacing: 0.65,
              textTransform: "uppercase",
            },
            "& .MuiChip-icon": {
              color: tone.color,
              ml: 0.35,
              mr: -0.15,
            },
          }}
        />
      </Box>
    </Grow>
  );
};

const ToolDebugMessage = ({ content }: { content: string }) => {
  const [open, setOpen] = useState(false);
  const summary = summarizeToolContent(content);
  const tone = getToolTone(content);

  return (
    <Grow in timeout={220}>
      <Paper
        elevation={0}
        sx={{
          p: 0.9,
          width: "100%",
          borderRadius: 2,
          border: "1px solid",
          borderColor: tone.border,
          bgcolor: tone.background,
          color: "text.primary",
          overflow: "hidden",
        }}
      >
        <Stack spacing={0.5}>
          <Stack direction="row" alignItems="center" gap={0.85} minWidth={0}>
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: "999px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                color: tone.color,
                bgcolor: alpha(tone.color, 0.14),
                border: `1px solid ${tone.border}`,
              }}
            >
              {tone.icon}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="overline"
                sx={{
                  display: "block",
                  color: tone.color,
                  lineHeight: 1,
                  letterSpacing: 0.8,
                  fontSize: 10,
                }}
              >
                debug
              </Typography>
              <Typography
                variant="caption"
                fontWeight={700}
                noWrap
                sx={{ lineHeight: 1.15, fontSize: 12.5 }}
              >
                {summary.title}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              sx={{
                px: 0.85,
                py: 0.2,
                borderRadius: 999,
                fontWeight: 800,
                color: tone.color,
                bgcolor: alpha(tone.color, 0.12),
                border: `1px solid ${tone.border}`,
                lineHeight: 1,
                textTransform: "uppercase",
                letterSpacing: 0.6,
              }}
            >
              {tone.label}
            </Typography>
          </Stack>

          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              whiteSpace: "pre-wrap",
              lineHeight: 1.3,
              fontSize: 11.5,
            }}
          >
            {summary.detail}
          </Typography>

          <Button
            size="small"
            variant="text"
            onClick={() => setOpen(true)}
            sx={{
              alignSelf: "flex-start",
              px: 0,
              minWidth: 0,
              color: tone.color,
              fontWeight: 700,
              textTransform: "none",
              fontSize: 11.5,
            }}
          >
            Ver detalle
          </Button>

          <Dialog
            open={open}
            onClose={() => setOpen(false)}
            fullWidth
            maxWidth="sm"
            PaperProps={{
              sx: {
                overflow: "hidden",
              },
            }}
          >
            <DialogTitle>
              <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "999px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: tone.color,
                    bgcolor: alpha(tone.color, 0.14),
                    border: `1px solid ${tone.border}`,
                    flexShrink: 0,
                  }}
                >
                  {tone.icon}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="overline" sx={{ color: tone.color, lineHeight: 1 }}>
                    debug
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={800} noWrap>
                    {summary.title}
                  </Typography>
                </Box>
              </Stack>
            </DialogTitle>
            <DialogContent dividers sx={{ pt: 2 }}>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", whiteSpace: "pre-wrap", mb: 1.5 }}
              >
                {summary.detail}
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.25,
                  borderRadius: 2,
                  bgcolor: "rgba(0,0,0,0.16)",
                  color: "text.primary",
                  fontSize: 12,
                  lineHeight: 1.5,
                  fontFamily:
                    'ui-monospace, SFMono-Regular, SF Mono, Consolas, "Liberation Mono", Menlo, monospace',
                  overflow: "auto",
                  maxHeight: 360,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {summary.raw}
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setOpen(false)} variant="contained">
                Cerrar
              </Button>
            </DialogActions>
          </Dialog>
        </Stack>
      </Paper>
    </Grow>
  );
};

const ToolLegacyMessage = ({ message }: { message: { content: string } }) => {
  const { content } = message;
  const [open, setOpen] = useState(false);
  const summary = summarizeToolContent(content);
  const tone = getToolTone(content);
  const hasDetails =
    summary.isJson || summary.raw.length > 120 || /\n/.test(summary.raw);

  return (
    <Grow in timeout={220}>
      <Paper
        elevation={0}
        sx={{
          p: 1.1,
          width: "100%",
          borderRadius: 2.5,
          border: "1px solid",
          borderColor: tone.border,
          bgcolor: tone.background,
          color: "text.primary",
          overflow: "hidden",
        }}
      >
        <Stack spacing={0.7}>
          <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "999px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                color: tone.color,
                bgcolor: alpha(tone.color, 0.14),
                border: `1px solid ${tone.border}`,
              }}
            >
              {tone.icon}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="overline"
                sx={{
                  display: "block",
                  color: tone.color,
                  lineHeight: 1.1,
                  letterSpacing: 0.9,
                }}
              >
                debug
              </Typography>
              <Typography
                variant="body2"
                fontWeight={700}
                noWrap
                sx={{ lineHeight: 1.2 }}
              >
                {summary.title}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              sx={{
                px: 1,
                py: 0.35,
                borderRadius: 999,
                fontWeight: 800,
                color: tone.color,
                bgcolor: alpha(tone.color, 0.12),
                border: `1px solid ${tone.border}`,
                lineHeight: 1,
                textTransform: "uppercase",
                letterSpacing: 0.6,
              }}
            >
              {tone.label}
            </Typography>
          </Stack>

          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              whiteSpace: "pre-wrap",
              lineHeight: 1.45,
            }}
          >
            {summary.detail}
          </Typography>

          {hasDetails && (
            <Button
              size="small"
              variant="text"
              onClick={() => setOpen((current) => !current)}
              sx={{
                alignSelf: "flex-start",
                px: 0,
                minWidth: 0,
                color: tone.color,
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              {open ? "Ocultar detalle" : "Ver detalle"}
            </Button>
          )}

          <Dialog
            open={open}
            onClose={() => setOpen(false)}
            fullWidth
            maxWidth="sm"
            PaperProps={{
              sx: {
                overflow: "hidden",
              },
            }}
          >
            <DialogTitle>Detalles del evento</DialogTitle>
            <DialogContent dividers>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1,
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  color: "text.primary",
                  fontSize: 12,
                  lineHeight: 1.5,
                  fontFamily:
                    'ui-monospace, SFMono-Regular, SF Mono, Consolas, "Liberation Mono", Menlo, monospace',
                  overflow: "auto",
                  maxHeight: 360,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {summary.raw}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpen(false)}>Cerrar</Button>
            </DialogActions>
          </Dialog>
        </Stack>
      </Paper>
    </Grow>
  );
};

export const MessageItem: React.FC<MessageItemProps> = ({
  message,
  toolDisplayMode = "legacy",
  isAssistant,
  onRequestAudioTranscription,
}) => {
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [isTranscribingAudio, setIsTranscribingAudio] = useState(false);
  const parsedMetadata = safeParse(message.contentMetadata);

  switch (message.messageType) {
    case MessageTypeEnum.TEXT:
      return <Typography variant="body2">{message.content}</Typography>;

    case MessageTypeEnum.IMAGE:
      {
        const imageUrl = resolveMediaUrl(message, parsedMetadata);
        const caption =
          typeof parsedMetadata?.caption === "string"
            ? parsedMetadata.caption.trim()
            : "";
        if (!imageUrl) {
          return (
            <Box>
              <MediaPendingState
                failed={parsedMetadata?.processingStatus === "FAILED"}
                label="La imagen"
              />
              {caption && (
                <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: "pre-wrap" }}>
                  {caption}
                </Typography>
              )}
            </Box>
          );
      }
      return (
        <>
          <Box
            sx={{
              width: "fit-content",
              maxWidth: "100%",
              overflow: "hidden",
              bgcolor: caption ? "#ffffff" : "transparent",
              borderRadius: caption ? "0 0 14px 14px" : 0,
            }}
          >
            <img
              src={imageUrl}
              alt="Imagen enviada"
              style={{
                maxWidth: "100%",
                borderRadius: 0,
                maxHeight: 300,
                cursor: "pointer",
                display: "block",
              }}
              onClick={() => setOpenImageDialog(true)}
            />
            {caption && (
              <Typography
                variant="body2"
                sx={{
                  px: 1.25,
                  pt: 0.9,
                  pb: 1.05,
                  color: "#1f2937",
                  lineHeight: 1.4,
                  whiteSpace: "pre-wrap",
                }}
              >
                {caption}
              </Typography>
            )}
          </Box>
          <Dialog
            open={openImageDialog}
            onClose={() => setOpenImageDialog(false)}
            maxWidth="md"
          >
            <img
              src={imageUrl}
              alt="Imagen ampliada"
              style={{
                maxWidth: "100%",
                maxHeight: "80vh",
                margin: "auto",
                display: "block",
              }}
            />
          </Dialog>
        </>
      );
      }

    case MessageTypeEnum.AUDIO: {
      const audioUrl = resolveMediaUrl(message, parsedMetadata);
      const transcription =
        parsedMetadata?.transcriptionRequestedAt && parsedMetadata?.transcription
          ? parsedMetadata.transcription
          : "";
      const processingStatus = parsedMetadata?.processingStatus;
      const isUrl = Boolean(audioUrl);
      const canRequestTranscription =
        Boolean(onRequestAudioTranscription) &&
        isUrl &&
        !transcription &&
        processingStatus !== "PENDING";

      return (
        <Box
          p={0.3}
          sx={{
            width: "100%",
            maxWidth: 277,
            display: "flex",
            flexDirection: "column",
            gap: 0.45,
          }}
        >
          <AudioMiniPlayer
            src={isUrl ? audioUrl : undefined}
            failed={processingStatus === "FAILED"}
            isAssistant={isAssistant}
          />
          {canRequestTranscription && (
            <Box>
              <Button
                size="small"
                variant="contained"
                disabled={isTranscribingAudio}
                onClick={async () => {
                  if (!onRequestAudioTranscription) return;
                  try {
                    setIsTranscribingAudio(true);
                    await onRequestAudioTranscription(message);
                  } finally {
                    setIsTranscribingAudio(false);
                  }
                }}
                sx={{
                  alignSelf: "flex-start",
                  px: 1,
                  py: 0.24,
                  borderRadius: 999,
                  bgcolor: "rgba(255, 255, 255, 0.94)",
                  color: "#7b1fa2",
                  fontSize: 9.5,
                  fontWeight: 800,
                  minHeight: 21,
                  lineHeight: 1,
                  textTransform: "none",
                  boxShadow: "0 4px 14px rgba(49, 15, 76, 0.18)",
                  "&:hover": {
                    bgcolor: "#ffffff",
                    boxShadow: "0 6px 18px rgba(49, 15, 76, 0.24)",
                  },
                  "&.Mui-disabled": {
                    bgcolor: "rgba(255, 255, 255, 0.72)",
                    color: "rgba(123, 31, 162, 0.62)",
                  },
                }}
              >
                {isTranscribingAudio ? "Transcribiendo..." : "Transcribir"}
              </Button>
            </Box>
          )}
          {transcription && (
            <Box
              sx={{
                width: 270,
                maxWidth: "100%",
                color: "#ffffff",
              }}
            >
              <Typography
                component="div"
                sx={{
                  fontSize: 12.75,
                  fontWeight: 800,
                  lineHeight: 1.15,
                  mb: 0.3,
                }}
              >
                Transcripción:
              </Typography>
              <Typography
                sx={{
                  fontSize: 11.9,
                  lineHeight: 1.3,
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                  whiteSpace: "pre-wrap",
                }}
              >
                {transcription}
              </Typography>
            </Box>
          )}
        </Box>
      );
    }

    case MessageTypeEnum.VIDEO: {
      const videoUrl = resolveMediaUrl(message, parsedMetadata);
      const isGif = isGifMedia(message, parsedMetadata);
      if (!videoUrl) {
        return (
          <VideoPendingPreview
            failed={parsedMetadata?.processingStatus === "FAILED"}
            isGif={isGif}
          />
        );
      }
      if (isGif) {
        return <GifPreview src={videoUrl} caption={parsedMetadata?.caption} />;
      }
      return (
        <VideoPlayerPreview
          src={videoUrl}
          caption={parsedMetadata?.caption}
          isAssistant={isAssistant}
        />
      );
    }

    case MessageTypeEnum.DOCUMENT: {
      const fileUrl = resolveMediaUrl(message, parsedMetadata);
      const fileName = parsedMetadata?.fileName || message.content || "Documento";
      return (
        <DocumentPreview
          fileUrl={fileUrl}
          fileName={fileName}
          mimeType={parsedMetadata?.mimeType || parsedMetadata?.mime_type}
          caption={parsedMetadata?.caption}
          failed={parsedMetadata?.processingStatus === "FAILED"}
        />
      );
    }

    case MessageTypeEnum.STICKER: {
      const stickerUrl = resolveMediaUrl(message, parsedMetadata);
      if (!stickerUrl) {
        return (
          <MediaPendingState
            failed={parsedMetadata?.processingStatus === "FAILED"}
            label="El sticker"
            loader
          />
        );
      }
      return (
        <Box sx={{ lineHeight: 0 }}>
          <img
            src={stickerUrl}
            alt="Sticker"
            style={{ maxWidth: 180, maxHeight: 180, display: "block" }}
          />
        </Box>
      );
    }

    case MessageTypeEnum.LOCATION:
      try {
        const locationMetadata = safeParse(message.contentMetadata) || {};
        const { latitude, longitude, name, address } = locationMetadata;
        return (
          <LocationPreview
            latitude={latitude}
            longitude={longitude}
            name={name}
            address={address}
          />
        );
      } catch (error) {
        return <LocationPreview />;
      }

    case MessageTypeEnum.CONTACTS: {
      const contacts = Array.isArray(parsedMetadata) ? parsedMetadata : [];
      return (
        <Stack spacing={0.8}>
          <Stack direction="row" spacing={1} alignItems="center">
            <LocationOnOutlined fontSize="small" sx={{ visibility: "hidden", width: 0 }} />
            <Typography variant="body2" fontWeight={700}>
              Contactos compartidos
            </Typography>
          </Stack>
          {contacts.length ? contacts.map((contact: any, index: number) => {
            const name =
              contact?.name?.formatted_name ||
              contact?.name?.first_name ||
              contact?.org?.company ||
              `Contacto ${index + 1}`;
            const phone =
              Array.isArray(contact?.phones) && contact.phones.length
                ? contact.phones.map((item: any) => item?.phone).filter(Boolean).join(", ")
                : "";
            return (
              <Box key={`${name}-${index}`}>
                <Typography variant="body2" fontWeight={600}>
                  {name}
                </Typography>
                {phone && (
                  <Typography variant="caption" color="text.secondary">
                    {phone}
                  </Typography>
                )}
              </Box>
            );
          }) : (
            <Typography variant="body2">{message.content}</Typography>
          )}
        </Stack>
      );
    }

    case MessageTypeEnum.REACTION:
      return (
        <Stack direction="row" spacing={0.8} alignItems="center">
          <ReplyOutlined fontSize="small" />
          <Typography variant="body2">{message.content}</Typography>
        </Stack>
      );

    case MessageTypeEnum.INFO: {
      const metadata = safeParse(message.contentMetadata);

      switch (metadata?.type) {
        case "SUPPORT_TICKET_CREATED":
          return (
            <Stack spacing={0.35}>
              <Stack direction="row" spacing={0.7} alignItems="center">
                <ConfirmationNumber fontSize="small" color="secondary" />
                <Typography variant="body2" fontWeight={700}>
                  Ticket de soporte creado
                </Typography>
              </Stack>
              {metadata.ticketCode && (
                <Typography variant="caption" color="text.secondary">
                  Código: {metadata.ticketCode}
                </Typography>
              )}
              <Typography variant="body2">
                {metadata.description || message.content}
              </Typography>
              {metadata.orderId && (
                <Typography variant="caption" color="text.secondary">
                  Pedido: {metadata.orderId}
                </Typography>
              )}
            </Stack>
          );

        case "SUPPORT_TICKET_STATUS_CHANGED": {
          const statusLabel =
            metadata.status === "IN_PROGRESS"
              ? "en proceso"
              : metadata.status === "RESOLVED"
              ? "resuelto"
              : "reabierto";
          return (
            <Stack direction="row" spacing={0.7} alignItems="center">
              <ConfirmationNumber fontSize="small" color="secondary" />
              <Typography variant="body2">
                Ticket {metadata.ticketCode ? `#${metadata.ticketCode} ` : ""}
                <strong>{statusLabel}</strong>
              </Typography>
            </Stack>
          );
        }

        case "PROGRAM_REQUIRED":
          return (
            <Box bgcolor="info.light" p={1} borderRadius={2}>
              <Typography variant="body2">{message.content}</Typography>
              <Box mt={1}>
                <Typography variant="subtitle2">Fechas disponibles:</Typography>
              </Box>
            </Box>
          );

        case "STORES_OPENED":
          return (
            <Box bgcolor="info.light" p={1} borderRadius={2}>
              <StoresOpenedWidget data={metadata.data} />
            </Box>
          );

        default:
          return (
            <Box bgcolor="info.light" p={1} borderRadius={2}>
              <Typography variant="body2">{message.content}</Typography>
            </Box>
          );
      }
    }

    case MessageTypeEnum.TOOL:
      if (toolDisplayMode === "compact") {
        return <ToolCompactMessage content={message.content} />;
      }

      if (toolDisplayMode === "debug") {
        return <ToolDebugMessage content={message.content} />;
      }

      return <ToolLegacyMessage message={{ content: message.content }} />;

    case MessageTypeEnum.INTERACTIVE:
      return <InteractiveMessage message={message} />;

    case MessageTypeEnum.SYSTEM:
      return (
        <Typography variant="caption" color="text.secondary">
          {message.content}
        </Typography>
      );

    default:
      return (
        <Stack direction="row" spacing={0.8} alignItems="center">
          <PlayCircleOutline fontSize="small" sx={{ display: "none" }} />
          <StickyNote2Outlined fontSize="small" />
          <Typography variant="body2">
            {message.content || "[Tipo de mensaje desconocido]"}
          </Typography>
        </Stack>
      );
  }
};
